// ** React Imports
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import React, { useMemo } from "react";
// ** MUI Components
import { Box, Typography, Tooltip, AvatarGroup, Avatar, IconButton } from "@mui/material";

// ** Icon Imports
import Icon from 'components/icon';

// ** Custom Component Imports
import CustomChip from "components/mui/chip";

// ** Third-Party Imports
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";

// ** Logic Imports
import { GetAllJobs } from "./GetAllJobs";
import { GetWorkReqService } from "screens/Requests/logic/GetWorkRequirement";
import { useWorkRequirementLogic } from "./work-requirement-logic";
import { GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";

export const useJobListLogic = (requestID = 0, workReqID = 0, setLoading) => {
  //#region Hooks
  const navigate = useNavigate();
  //#endregion Hooks
  
  //#region States
  const [jobList, setJobList] = useState([]);
  const [jobListPaginationModel, setJobListPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [openJobDetail, setOpenJobDetail] = useState(false);
  const [selectedWorkReq, setSelectedWorkReq] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [serviceRequest, setServiceRequest] = useState({});
  const [rules, setRules] = useState(
    { 
      canEdit: true,
      canDelete: true,
      canSubmit: true,
      nextStatuses: [],
      cannotGoBack: false
  });
  //#endregion States

  //#region Props Destructuring
  const {
    WorkRequirementsProps
  } = useWorkRequirementLogic({requestID, navigate, toast, setLoading, serviceRequest});

  const {
    fetchWorkRequirements
  } = WorkRequirementsProps;
  //#endregion Props Destructuring

  //#region Handlers
  const fetchJobs = async () => {
    setLoading(true);
    try{
      const result = await GetAllJobs(workReqID, requestID, navigate);
      if(result){
        const formattedData = result.map((job, index) => ({
          ...job,
          id: job.jobID
        }))
        setJobList(formattedData);
      }
    }catch(error){
      console.error('Error fetching jobs:', error)
    }finally{
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs();
  }, [])

  const handleOpenJobDetail = async (requestID, workReqID, jobID, customerID) => {
    const serviceReq = await GetServiceRequests(customerID, requestID, 0);
    const workRequirementList = await GetWorkReqService(requestID, 0, navigate);
    
    const workReq = workRequirementList?.find((wr) => wr.workReqID === workReqID);
    const job = workReq?.jobs.find((j) => j.jobID === jobID);
    
    setServiceRequest(serviceReq);
    setSelectedWorkReq(workReq);
    setSelectedJob(job);
    setOpenJobDetail(true);
    fetchJobs();
  };

  const handleCloseJobDetail = () => {    
    setOpenJobDetail(false);
    setSelectedWorkReq(null);
    setSelectedJob(null);
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '';
    
    return name
      .split(' ')
      .filter(part => part.trim().length > 0)
      .map(part => part[0].toUpperCase())
      .join('');
  };
  //#endregion Handlers

  //#region Column Configuration
  const columnsJobList = [
    {
      flex: 0.1,
      field: "jobID",
      minWidth: 120,
      headerName: "Job ID",
      renderCell: ({ row }) => {
        const { requestID, workReqID, jobID, customerID } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography 
              sx={{ 
                flexGrow: 1,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={() => handleOpenJobDetail(requestID, workReqID, jobID, customerID)}
            >
              {jobID}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "requestID",
      minWidth: 180,
      headerName: "Work Order ID",
      renderCell: ({ row }) => {
        const { requestID } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>WRO-{requestID}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "jobsiteAddress",
      minWidth: 140,
      headerName: "Jobsite",
      renderCell: ({ row }) => {
        const { jobsiteAddress, jobsiteName } = row;

        return (
          <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <Typography
            noWrap
            variant="body2"
            sx={{ color: "text.primary", fontWeight: 600 }}
          >
            {jobsiteName || ''}
          </Typography>
          <Typography noWrap color="text.secondary" fontSize="small">
            {jobsiteAddress || ''}
          </Typography>
        </Box>
        );
      },
    },
     {
      flex: 0.1,
      field: "industryname",
      minWidth: 180,
      headerName: "Industry",
      renderCell: ({ row }) => {
        const { industry} = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{industry || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "service",
      minWidth: 140,
      headerName: "Service",
      renderCell: ({ row }) => {
        const { service } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{service || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "customerName",
      headerName: "Customer Name",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { customerName , customerID } = row;

        return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%",
             cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
          }, }} 
          onClick={() => navigate(`/customers/detail/${customerID}`)}>
            <Typography sx={{ flexGrow: 1 }}>{customerName || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "labor",
      headerName: "Labor",
      minWidth: 210,
      renderCell: ({ row }) => {
        const { labor } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {labor && labor.length > 0 ? (
                <AvatarGroup
                  className="pull-up"
                  max={4}
                  sx={{
                    "& .MuiAvatar-root": { width: 25, height: 25 },
                    "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                  }}
                >
                  {labor.map((emp) => (
                    <Tooltip key={emp.employeeID} title={emp.employeeName}>
                      <Avatar
                        src="/images/avatars/4.png"
                        alt="employee-picture"
                        sx={{ width: 25, height: 25, fontSize: '12px' }}
                      >{getInitials(emp.employeeName)}</Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
            ) : (<Typography variant='body2' color='text.secondary'>-</Typography>)}
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "scheduleStart",
      headerName: "Scheduled Start",
      minWidth: 240,
      renderCell: ({ row }) => {
        const { scheduleStartDate, scheduleStartTime } = row;
        
        let displayText = '-';
        if (scheduleStartDate) {
          displayText = dayjs(scheduleStartDate).format('MM/DD/YYYY');
          if (scheduleStartTime) {
            const time = dayjs(`1970-01-01T${scheduleStartTime}`);
            displayText += ` ${time.format('hh:mm A')}`;
          }
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{displayText}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "scheduleEnd",
      headerName: "Scheduled End",
      minWidth: 240,
      renderCell: ({ row }) => {
        const { scheduleEndDate, scheduleEndTime } = row;
        
        let displayText = '-';
        if (scheduleEndDate) {
          displayText = dayjs(scheduleEndDate).format('MM/DD/YYYY');
          if (scheduleEndTime) {
            const time = dayjs(`1970-01-01T${scheduleEndTime}`);
            displayText += ` ${time.format('hh:mm A')}`;
          }
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{displayText}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualStart",
      headerName: "Actual Start",
      minWidth: 220,
      renderCell: ({ row }) => {
        const { actualStart } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{ actualStart ? dayjs(actualStart).format('MM/DD/YYYY hh:mm A') : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualEnd",
      headerName: "Actual End",
      minWidth: 220,
      renderCell: ({ row }) => {
        const { actualEnd } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{ actualEnd ? dayjs(actualEnd).format('MM/DD/YYYY hh:mm A') : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "scheduledDuration",
      headerName: "Scheduled Duration",
      minWidth: 200,
      renderCell: ({ row }) => {
        const { scheduledDuration } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{scheduledDuration ? `${scheduledDuration} Hrs` : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualDuration",
      headerName: "Actual Duration",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { actualDuration } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{actualDuration ? `${actualDuration} Hrs` : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { status } = row;

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={
              status === "Not Viewed" || status === "Under Review"
                ? "info"
                : status === "Completed" || status === "Submitted"
                ? "success"
                : status === "On Hold" || status === "In Progress"
                ? "warning"
                : status === "Canceled" || status === "Closed"
                ? "error"
                : "secondary"
            }
            label={status}
          />
        );
      },
    },
  ];

 const columnsJobListDetail = useMemo(() => [
    {
      flex: 0.1,
      field: "jobID",
      minWidth: 120,
      headerName: "Job ID",
      renderCell: ({ row }) => {
        console.log(row);
        const { requestID, workReqID, jobID, customerID } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography 
              sx={{ 
                flexGrow: 1,
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={() => handleOpenJobDetail(requestID, workReqID, jobID, customerID)}
            >
              {jobID}
            </Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "industryname",
      minWidth: 180,
      headerName: "Industry",
      renderCell: ({ row }) => {
        const { industry} = row;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ flexGrow: 1 }}>{industry || '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "service",
      minWidth: 140,
      headerName: "Service",
      renderCell: ({ row }) => {
        const { service } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{service}</Typography>
          </Box>
        );
      },
    },
    
    {
      flex: 0.1,
      field: "labor",
      headerName: "Labor",
      minWidth: 210,
      renderCell: ({ row }) => {
        const { labor } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {labor && labor.length > 0 ? (
                <AvatarGroup
                  className="pull-up"
                  max={4}
                  sx={{
                    "& .MuiAvatar-root": { width: 25, height: 25 },
                    "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                  }}
                >
                  {labor.map((emp) => (
                    <Tooltip key={emp.employeeID} title={emp.employeeName}>
                      <Avatar
                        src="/images/avatars/4.png"
                        alt="employee-picture"
                        sx={{ width: 25, height: 25, fontSize: '12px' }}
                      >{getInitials(emp.employeeName)}</Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
            ) : (<Typography variant='body2' color='text.secondary'>-</Typography>)}
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "scheduleStart",
      headerName: "Scheduled Start",
      minWidth: 240,
      renderCell: ({ row }) => {
        const { scheduleStartDate, scheduleStartTime } = row;
        
        let displayText = '-';
        if (scheduleStartDate) {
          displayText = dayjs(scheduleStartDate).format('MM/DD/YYYY');
          if (scheduleStartTime) {
            const time = dayjs(`1970-01-01T${scheduleStartTime}`);
            displayText += ` ${time.format('hh:mm A')}`;
          }
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{displayText}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "scheduleEnd",
      headerName: "Scheduled End",
      minWidth: 240,
      renderCell: ({ row }) => {
        const { scheduleEndDate, scheduleEndTime } = row;
        
        let displayText = '-';
        if (scheduleEndDate) {
          displayText = dayjs(scheduleEndDate).format('MM/DD/YYYY');
          if (scheduleEndTime) {
            const time = dayjs(`1970-01-01T${scheduleEndTime}`);
            displayText += ` ${time.format('hh:mm A')}`;
          }
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{displayText}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualStart",
      headerName: "Actual Start",
      minWidth: 220,
      renderCell: ({ row }) => {
        const { actualStart } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{ actualStart ? dayjs(actualStart).format('MM/DD/YYYY hh:mm A') : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualEnd",
      headerName: "Actual End",
      minWidth: 220,
      renderCell: ({ row }) => {
        const { actualEnd } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{ actualEnd ? dayjs(actualEnd).format('MM/DD/YYYY hh:mm A') : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "scheduledDuration",
      headerName: "Scheduled Duration",
      minWidth: 200,
      renderCell: ({ row }) => {
        const { scheduledDuration } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{scheduledDuration ? `${scheduledDuration} Hrs` : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "actualDuration",
      headerName: "Actual Duration",
      minWidth: 180,
      renderCell: ({ row }) => {
        const { actualDuration } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{actualDuration ? `${actualDuration} Hrs` : '-'}</Typography>
          </Box>
        );
      },
    },
    {
      flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { status } = row;

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={
              status === "Not Viewed" || status === "Under Review"
                ? "info"
                : status === "Completed" || status === "Submitted"
                ? "success"
                : status === "On Hold" || status === "In Progress"
                ? "warning"
                : status === "Canceled" || status === "Closed"
                ? "error"
                : "secondary"
            }
            label={status}
          />
        );
      },
    },
 ], []);
  //#endregion Column Configuration

  const JobListProps = {
    jobList,
    columnsJobListDetail,
    columnsJobList,
    jobListPaginationModel,
    setJobListPaginationModel,
    openJobDetail,
    handleCloseJobDetail,
    selectedWorkReq,
    selectedJob,
    setSelectedJob,
    fetchWorkRequirements,
    rules,
    fetchJobs
  };

  return {
    JobListProps,
  };
};
